import LFCSelectFormJudge from '_components-with-data/inputs/LFCSelectFormJudge';
import LFCAutocomplete, {
  ChangeParameter,
  multiSelectData
} from '_components/inputs/LFCAutocomplete';
import LFCButton from '_components/inputs/LFCButton';
import LFCDatetimePicker from '_components/inputs/LFCDatetimePicker';
import LFCTextField from '_components/inputs/LFCTextField';
import LFCFormRowGroup from '_components/layout/LFCFormRowGroup';
import ProgressBar from '_components/ProgressBar';
import LFCSavedCondition from '_components/search-conditions/LFCSavedCondition';
import {useGetDefaultCondition} from '_contexts/SavedConditionProvider';
import {fmtYMD} from '_logics/LFCFormatUtil';
import {LOCATIONID_LQ_INSPECTIONRESULTS_MAIN, PAGEID_LQ_INSPECTIONRESULTS} from '_logics/LFCPageId';
import {getLFCData} from '_logics/LFCUtil';
import axios, {CancelTokenSource} from 'axios';
import dayjs from 'dayjs';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {SearchConditions} from './types';

// 初期値を返す関数として定義（毎回新しいオブジェクトを生成）
const getSearchConditionsInit = () => ({
  selectDateFrom: dayjs().add(-7, 'd').format('YYYY-MM-DD HH:mm'),
  selectDateTo: dayjs().format('YYYY-MM-DD HH:mm'),
  judge: 9,
  work: [],
  serialno: ''
});

interface SearchPanelProps {
  onSearch: (searchConditions: SearchConditions) => void;
}

export const SearchPanel = React.memo(({onSearch}: SearchPanelProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const [startProcess, setStartProcess] = useState<boolean>(false);
  const cancelSource = useRef<CancelTokenSource | null>(null);
  const formRef = useRef<HTMLFormElement>(null!);

  const getDefaultCondition = useGetDefaultCondition();
  const [searchConditions, setSearchConditions] = useState<SearchConditions>(
    getDefaultCondition(PAGEID_LQ_INSPECTIONRESULTS, LOCATIONID_LQ_INSPECTIONRESULTS_MAIN) ??
      getSearchConditionsInit()
  );

  const [isAutoCompleteReset, setIsAutoCompleteReset] = useState<boolean>(false);
  const [workList, setWorkList] = useState<{label: string}[]>([]);

  /**
   * 入力値変更
   */
  const handleChange = useCallback((event: any) => {
    setSearchConditions(prev => ({...prev, [event.target.name]: event.target.value}));
  }, []);

  /**
   * 入力値変更
   */
  const handleAutoChange = useCallback((relayDatas: ChangeParameter) => {
    setSearchConditions(prev => ({...prev, [relayDatas.name]: relayDatas.data}));
  }, []);

  /**
   * 検索処理（内部用）
   */
  const executeSearch = (conditions: SearchConditions) => {
    if (!conditions.serialno) {
      if (!formRef.current?.reportValidity()) {
        return;
      }
    }
    onSearch(conditions);
  };

  /**
   * 検索処理（ボタンクリック用）
   */
  const handleSearch = () => {
    executeSearch(searchConditions);
  };

  /**
   * リセット処理
   */
  const handleReset = () => {
    setSearchConditions(getSearchConditionsInit());
  };

  /**
   * シリアルNo入力時の他の検索値をクリアする
   */
  useEffect(() => {
    if (searchConditions.serialno !== '') {
      setSearchConditions(prev => ({
        ...prev,
        selectDateFrom: '',
        selectDateTo: '',
        judge: 9,
        work: []
      }));
    }
  }, [searchConditions.serialno]);

  /**
   * 初期処理
   */
  useEffect(() => {
    setStartProcess(true);

    cancelSource.current = axios.CancelToken.source();
    Promise.allSettled([
      //機種
      getLFCData({
        snack: enqueueSnackbar,
        sql_id: 40001,
        parameters: {},
        t,
        cancelToken: cancelSource.current.token
      }).then(ds => {
        const tmp = ds.map((item: {work: string}) => ({label: item.work}));
        setWorkList(tmp);
      })
    ]).finally(() => {
      setStartProcess(false);
    });

    return () => {
      // アンマウント処理
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  const [openSavedCondition, setOpenSavedCondition] = useState(false);
  const [savedCondition, setSavedCondition] = useState<any[]>([]);

  /**
   * 保存した検索条件ダイアログを開く
   */
  const handleLoadSavedCondition = useCallback(() => {
    // input check
    if (!searchConditions.serialno) {
      if (!formRef.current?.reportValidity()) {
        return;
      }
    }
    // 条件を毎回新しく作成（LFCSavedConditionコンポーネントのuseEffect依存配列を正しく動作させるため）
    setSavedCondition([
      {
        name: 'selectDateFrom',
        value: searchConditions.selectDateFrom,
        valueLabel: fmtYMD(dayjs(searchConditions.selectDateFrom)) || '',
        colName: t('対象期間From'),
        colWidth: 150
      },
      {
        name: 'selectDateTo',
        value: searchConditions.selectDateTo,
        valueLabel: fmtYMD(dayjs(searchConditions.selectDateTo)) || '',
        colName: t('対象期間To'),
        colWidth: 150
      },
      {
        name: 'judge',
        value: searchConditions.judge,
        valueLabel:
          searchConditions.judge === 9 ? t('すべて') : searchConditions.judge === 0 ? 'OK' : 'NG',
        colName: t('総合判定'),
        colWidth: 100
      },
      {
        name: 'work',
        value: searchConditions.work,
        valueLabel: searchConditions.work.join(','),
        colName: t('機種'),
        colWidth: 300
      },
      {
        name: 'serialno',
        value: searchConditions.serialno,
        valueLabel: searchConditions.serialno,
        colName: t('シリアルNo'),
        colWidth: 300
      }
    ]);
    setOpenSavedCondition(true);
  }, [searchConditions, t]);

  /**
   * 保存した検索条件を選択して検索実行
   */
  const handleSelectSavedCondition = (conditionValues: any) => {
    const updatedConditions = {...searchConditions, ...conditionValues};
    setOpenSavedCondition(false);
    setSearchConditions(updatedConditions);
    executeSearch(updatedConditions);
  };

  return (
    <>
      <ProgressBar startProcess={startProcess} screenLock />
      <form ref={formRef}>
        <LFCFormRowGroup>
          <LFCDatetimePicker
            name={'selectDateFrom'}
            label={t('対象期間From')}
            value={searchConditions.selectDateFrom}
            required
            onChange={handleChange}
          />
          <LFCDatetimePicker
            name={'selectDateTo'}
            label={t('対象期間To')}
            value={searchConditions.selectDateTo}
            required
            onChange={handleChange}
          />
          <LFCSelectFormJudge
            name={'judge'}
            value={searchConditions.judge}
            onChange={handleChange}
          />
          <LFCAutocomplete
            name={'work'}
            label={t('機種')}
            id={'work'}
            value={multiSelectData(workList, searchConditions.work)}
            onChange={handleAutoChange}
            onReset={isAutoCompleteReset}
            doneReset={setIsAutoCompleteReset}
            multiple={true}
            selectItem={workList}
          />
          <LFCTextField
            name="serialno"
            label={t('シリアルNo')}
            value={searchConditions.serialno}
            onChange={handleChange}
          />
          <LFCButton color="primary" onClick={handleSearch}>
            {t('検索')}
          </LFCButton>
          <LFCButton onClick={handleReset}>{t('リセット')}</LFCButton>
          <LFCSavedCondition
            open={openSavedCondition}
            pageId={PAGEID_LQ_INSPECTIONRESULTS}
            locationNo={LOCATIONID_LQ_INSPECTIONRESULTS_MAIN}
            onLoad={handleLoadSavedCondition}
            onSelect={handleSelectSavedCondition}
            onClose={() => setOpenSavedCondition(false)}
            conditions={savedCondition}
          />
        </LFCFormRowGroup>
      </form>
    </>
  );
});

SearchPanel.displayName = 'SearchPanel';
